import { useTracking } from '@folklore/tracking';
import { getComponentFromName } from '@folklore/utils';
import queryString from 'query-string';
import { ComponentType, useEffect, useRef, useState } from 'react';
import { useSearch } from 'wouter';

import { usePromotions } from '../../contexts/PromotionsContext';
import * as ModalsComponents from './modals';
import * as NotificationsComponents from './notifications';

type PromotionsPopupsProps = {
    minimumDelayBetween?: number | null;
};

const defaultMinimumDelayBetween = 30 * 1000;

function PromotionsPopups({
    minimumDelayBetween = defaultMinimumDelayBetween,
}: PromotionsPopupsProps) {
    const { promotions: activePromotions = [], allPromotions = [], markAsDone } = usePromotions();
    const [currentPromotion, setCurrentPromotion] = useState<Promotion | null>(null);
    const [lastPromotionTime, setLastPromotionTime] = useState<number | null>(null);

    const tracking = useTracking();
    const search = useSearch();
    const { promotion_popup: promotionPopupQueryRaw = null } = queryString.parse(search);
    const promotionPopupQuery =
        typeof promotionPopupQueryRaw === 'string' ? promotionPopupQueryRaw : null;

    const promotions = (activePromotions as Promotion[]).filter(
        ({ placement = null, trigger = null }) =>
            ['notification', 'modal'].indexOf((placement || '').split(':')[0]) !== -1 &&
            trigger !== null,
    );

    const updateCurrentPromotion = (newPromotion: Promotion | null) => {
        setCurrentPromotion(newPromotion);
        if (newPromotion === null && currentPromotion !== null) {
            setLastPromotionTime(new Date().getTime());
        }
    };

    const queryPromotion =
        promotionPopupQuery !== null
            ? ((allPromotions as Promotion[]).find(
                  ({ id, handle }) => handle === promotionPopupQuery || id === promotionPopupQuery,
              ) ?? null)
            : null;
    if (currentPromotion === null && queryPromotion !== null) {
        updateCurrentPromotion(queryPromotion);
    }

    if (
        currentPromotion !== null &&
        promotions.map(({ id }) => id).indexOf(currentPromotion.id) === -1
    ) {
        updateCurrentPromotion(null);
    }

    useEffect(() => {
        const currentTriggers = promotions.map(({ id }) => `promotion_popup_${id}`);
        tracking.triggers.getRegistered().forEach((trigger: string) => {
            if (
                trigger.match(/^promotion_popup_/) !== null &&
                currentTriggers.indexOf(trigger) === -1
            ) {
                tracking.triggers.unregister(trigger);
            }
        });
        promotions
            .filter(({ id }) => !tracking.triggers.isRegistered(`promotion_popup_${id}`))
            .forEach((promotion) => {
                const { id, trigger, force = false, priority = 0 } = promotion;
                tracking.triggers.register(`promotion_popup_${id}`, trigger, () => {
                    const now = new Date().getTime();
                    const elapsed = lastPromotionTime !== null ? now - lastPromotionTime : Infinity;
                    const { priority: currentPriority = 0 } = currentPromotion || {};
                    if (
                        (currentPromotion === null &&
                            (minimumDelayBetween === null || elapsed >= minimumDelayBetween)) ||
                        (force && priority > currentPriority)
                    ) {
                        updateCurrentPromotion(promotion);
                    }
                });
            });
        return () => {};
    }, [
        tracking,
        promotions,
        minimumDelayBetween,
        lastPromotionTime,
        updateCurrentPromotion,
        currentPromotion,
    ]);

    useEffect(
        () => () => {
            promotions.forEach(({ id }) => {
                tracking.triggers.unregister(`promotion_popup_${id}`);
            });
        },
        [],
    );

    useEffect(() => {
        if (currentPromotion !== null) {
            tracking.trackPromotion(currentPromotion, 'impression');
        }
    }, [currentPromotion, tracking]);

    const onClosed = () => {
        const { id: currentId = null, force = false } = currentPromotion || {};
        updateCurrentPromotion(null);
        if (!force && currentId !== null) {
            markAsDone(currentId);
        }
    };

    if (currentPromotion === null) {
        return null;
    }

    const { placement = null, type = null } = currentPromotion;
    const [, placementType = null] = (placement || '').split(':');

    const PromotionComponent = getComponentFromName(
        placement === 'notification' ? NotificationsComponents : ModalsComponents,
        placementType || type,
        'promotion',
    ) as ComponentType<{ promotion: Promotion; onClosed: () => void }> | null;

    return PromotionComponent !== null ? (
        <PromotionComponent promotion={currentPromotion} onClosed={onClosed} />
    ) : null;
}

export default PromotionsPopups;
